import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";

/**
 * Lightweight in-app product tour. Walks the user through a small list of
 * steps the first time they land on a feature-rich page. Driven entirely by
 * the `steps` prop — the same component is reusable across portals.
 *
 * Props:
 *   - storageKey: string — localStorage key used to remember "tour seen"
 *   - steps: [{ icon?, title, body, cta? }] — the carousel content
 *   - autoOpen: bool (default true) — open automatically on first visit
 *   - open / onOpenChange: optional controlled mode (e.g. "Replay tour" buttons)
 *   - onComplete: optional callback when the user finishes or skips
 */
export default function WelcomeTour({
  storageKey,
  steps = [],
  autoOpen = true,
  open: controlledOpen,
  onOpenChange,
  onComplete,
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [step, setStep] = useState(0);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
    if (!next) setStep(0);
  };

  useEffect(() => {
    if (!autoOpen || isControlled) return;
    if (!storageKey) return;
    try {
      if (!localStorage.getItem(storageKey)) {
        setInternalOpen(true);
      }
    } catch {
      // ignore (private mode etc.)
    }
  }, [autoOpen, isControlled, storageKey]);

  const markSeen = () => {
    try {
      if (storageKey) localStorage.setItem(storageKey, "1");
    } catch {
      // ignore
    }
  };

  const finish = () => {
    markSeen();
    setOpen(false);
    onComplete?.();
  };

  const total = steps.length;
  if (total === 0) return null;
  const current = steps[Math.min(step, total - 1)];
  const isLast = step >= total - 1;
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="welcome-tour">
        {/* Header band */}
        <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-transparent px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {Icon && (
                <div className="shrink-0 w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                  <Icon size={20} className="text-primary" weight="duotone" />
                </div>
              )}
              <div className="min-w-0">
                <DialogHeader className="space-y-0.5 text-left">
                  <DialogTitle className="font-[Chivo] text-base sm:text-lg leading-tight">
                    {current.title}
                  </DialogTitle>
                  <DialogDescription className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    Step {step + 1} of {total}
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>
            <button
              type="button"
              onClick={finish}
              className="shrink-0 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition"
              data-testid="welcome-tour-close"
              aria-label="Skip tour"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 text-sm text-muted-foreground leading-relaxed" data-testid={`welcome-tour-step-${step}`}>
          {current.body}
          {current.cta && (
            <div className="mt-4">{current.cta}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/30"}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="welcome-tour-back">
                <CaretLeft size={14} className="mr-1" /> Back
              </Button>
            )}
            {!isLast ? (
              <Button size="sm" onClick={() => setStep((s) => Math.min(total - 1, s + 1))} data-testid="welcome-tour-next">
                Next <CaretRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={finish} data-testid="welcome-tour-finish">
                Get started
              </Button>
            )}
          </div>
        </div>

        {/* Skip link */}
        {!isLast && (
          <button
            type="button"
            onClick={finish}
            className="absolute bottom-4 left-6 text-[11px] text-muted-foreground hover:text-foreground transition"
            data-testid="welcome-tour-skip"
          >
            Skip tour
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
